import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Wallet, BarChart3, CalendarDays, MessageCircle, ShieldCheck,
  CheckCircle2, ArrowRight, Heart, Moon, Sparkles, X, Send,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/format";
import { waLink } from "@/lib/whatsapp";
import { PromoCountdown } from "@/components/PromoCountdown";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1577877777751-3f1ec20a0715?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxtdXNpYyUyMHNjaG9vbCUyMHN0dWRlbnQlMjBwaWFub3xlbnwwfHx8fDE3OTAzOTkwNzB8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Users, title: "Database Siswa & Guru", desc: "Data siswa, grade, kelas, dan guru tersimpan rapi serta mudah dicari." },
  { icon: Wallet, title: "Pembayaran SPP & Kwitansi", desc: "Catat SPP, pendaftaran, buku, hingga lomba — kwitansi siap cetak otomatis." },
  { icon: BarChart3, title: "Laporan Keuangan Lengkap", desc: "Laba rugi, neraca, kas kecil, dan jurnal manual. Export ke Excel & PDF." },
  { icon: CalendarDays, title: "Jadwal Les & Absensi", desc: "Grid jadwal mingguan dan absensi per pertemuan dalam sekali klik." },
  { icon: MessageCircle, title: "Pengingat WhatsApp", desc: "Ingatkan tunggakan SPP ke orang tua siswa langsung via WhatsApp." },
  { icon: ShieldCheck, title: "Multi Pengguna & Peran", desc: "Akses terpisah untuk admin, staf keuangan, dan customer service." },
  { icon: Heart, title: "Portal Orang Tua", desc: "Orang tua memantau status SPP & kehadiran anak lewat tautan khusus — tanpa perlu login." },
  { icon: Moon, title: "Mode Gelap & Terang", desc: "Setiap sekolah bebas memilih tampilan favorit, tersimpan otomatis untuk semua staf." },
];

const STATS = [
  { value: "+500", label: "Sekolah Musik" },
  { value: "99.9%", label: "System Uptime" },
  { value: "24/7", label: "Priority Support" },
];

const HEADING = "font-['Manrope',sans-serif]";

function BrandLockup({ compact = false }) {
  return (
    <div className="flex items-center gap-3">
      <img
        src="/nexora-logo.jpg"
        alt="Nexora"
        className={`${compact ? "h-9 w-9" : "h-10 w-10"} rounded-xl object-cover shadow-[0_0_18px_rgba(0,209,168,0.25)]`}
        data-testid="brand-logo"
      />
      <div className="leading-tight">
        <p className={`${HEADING} ${compact ? "text-sm" : "text-base"} font-extrabold tracking-tight text-white`}>
          Aplikasi Sekolah Musik
        </p>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00D1A8]">by Nexora</p>
      </div>
    </div>
  );
}

const CHAT_CHIPS = ["Berapa harga paketnya?", "Apa saja fiturnya?", "Bagaimana cara mendaftar?"];

function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Halo! Saya asisten AI Nexora. Ada yang bisa saya bantu tentang Aplikasi Sekolah Musik?" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sid] = useState(() => {
    let v = localStorage.getItem("nexora_chat_sid");
    if (!v) {
      v = crypto.randomUUID();
      localStorage.setItem("nexora_chat_sid", v);
    }
    return v;
  });
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: msg }),
      });
      if (!res.ok) throw new Error("chat gagal");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "");
          if (!line) continue;
          try {
            const { delta } = JSON.parse(line);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + delta };
                return copy;
              });
            }
          } catch { /* abaikan */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Maaf, koneksi bermasalah. Silakan coba lagi atau hubungi kami via WhatsApp." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-chat-button"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#2563EB] to-[#00D1A8] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_0_30px_rgba(0,209,168,0.45)] transition-transform duration-200 hover:scale-105"
      >
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
        {open ? "Tutup" : "Tanya AI"}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 flex h-[480px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-2xl border border-white/15 bg-[#071731] shadow-2xl" data-testid="ai-chat-panel">
          <div className="flex items-center gap-3 border-b border-white/10 bg-[#040D1D] px-4 py-3">
            <img src="/nexora-logo.jpg" alt="Nexora" className="h-8 w-8 rounded-lg object-cover" />
            <div className="leading-tight">
              <p className={`${HEADING} text-sm font-bold text-white`}>Asisten AI Nexora</p>
              <p className="flex items-center gap-1 text-[11px] text-[#00D1A8]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00D1A8]" /> Online — menjawab otomatis
              </p>
            </div>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={m.role === "user"
                  ? "max-w-[80%] rounded-2xl rounded-br-sm bg-[#2563EB] px-3.5 py-2.5 text-sm text-white"
                  : "max-w-[85%] rounded-2xl rounded-bl-sm border border-white/10 bg-white/[0.06] px-3.5 py-2.5 text-sm leading-relaxed text-slate-200"}>
                  {m.text || (sending && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 px-4 pb-2">
              {CHAT_CHIPS.map((c) => (
                <button
                  key={c}
                  onClick={() => send(c)}
                  className="rounded-full border border-[#00D1A8]/30 bg-[#00D1A8]/10 px-3 py-1.5 text-xs text-[#00D1A8] transition-colors hover:bg-[#00D1A8]/20"
                  data-testid={`ai-chat-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-white/10 p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Tulis pertanyaan Anda..."
              maxLength={500}
              className="flex-1 rounded-xl border border-white/15 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-[#00D1A8]/60"
              data-testid="ai-chat-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-r from-[#2563EB] to-[#00D1A8] text-white transition-opacity disabled:opacity-50"
              data-testid="ai-chat-send-button"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function formatPhone(p) {
  const d = String(p || "").replace(/\D/g, "");
  if (d.startsWith("62") && d.length >= 11) {
    return `+62 ${d.slice(2, 5)}-${d.slice(5, 9)}-${d.slice(9)}`;
  }
  return p || "-";
}

export default function Landing() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState({ plans: [], product_name: "Aplikasi Sekolah Musik", owner_wa: "" });
  const [yearly, setYearly] = useState(false);

  useEffect(() => {
    api.get("/public/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const dashboardTo = user ? (user.role === "super_admin" ? "/superadmin" : "/dashboard") : null;
  const wa = cfg.owner_wa
    ? waLink(cfg.owner_wa, "Halo, saya tertarik dengan Aplikasi Sekolah Musik by Nexora untuk sekolah musik saya.")
    : null;

  return (
    <div className="min-h-screen bg-[#040D1D] font-['Plus_Jakarta_Sans',sans-serif] text-white" data-testid="landing-page">
      {/* ===== Navbar ===== */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#071731]/85 shadow-lg backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3.5">
          <BrandLockup />
          <nav className="hidden items-center gap-8 text-sm font-medium text-slate-300 md:flex">
            <a href="#fitur" className="transition-colors hover:text-white">Fitur</a>
            <a href="#harga" className="transition-colors hover:text-white">Harga</a>
            <a href="#kontak" className="transition-colors hover:text-white" data-testid="nav-contact-link">Hubungi Kami</a>
          </nav>
          <div className="flex items-center gap-2">
            {dashboardTo ? (
              <Link to={dashboardTo}>
                <Button className="bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_20px_rgba(0,209,168,0.3)] hover:opacity-95" data-testid="nav-dashboard-button">
                  Buka Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-300 hover:bg-white/10 hover:text-white" data-testid="nav-login-button">
                    Masuk
                  </Button>
                </Link>
                <Link to="/daftar">
                  <Button className="bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_20px_rgba(0,209,168,0.3)] hover:opacity-95" data-testid="nav-register-button">
                    Daftar Sekolah
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#071731] via-[#0B2A5B] to-[#040D1D]">
        <div className="pointer-events-none absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-[#2563EB]/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-1/3 h-80 w-80 rounded-full bg-[#00D1A8]/15 blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-6 py-20 sm:py-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#00D1A8]/30 bg-[#00D1A8]/10 px-4 py-1.5 text-xs font-semibold text-[#00D1A8]" data-testid="hero-badge">
              Digital • Technology • Innovation
            </span>
            <h1 className={`${HEADING} mt-6 text-4xl font-extrabold leading-[1.12] tracking-tight sm:text-5xl lg:text-6xl`}>
              Kelola Sekolah Musik Anda, Rapi &{" "}
              <span className="bg-gradient-to-r from-blue-400 to-[#00D1A8] bg-clip-text text-transparent">
                Modern
              </span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Database siswa, jadwal les, absensi, pembayaran SPP, sampai laporan keuangan lengkap —
              semua terintegrasi dalam satu platform berteknologi Nexora.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/daftar">
                <Button size="lg" className="gap-2 rounded-xl bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_25px_rgba(0,209,168,0.35)] hover:opacity-95" data-testid="hero-register-button">
                  Daftarkan Sekolah Anda <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <a href="#harga">
                <Button size="lg" variant="outline" className="rounded-xl border-white/20 bg-white/10 text-white hover:bg-white/20" data-testid="hero-pricing-button">
                  Lihat Paket Harga
                </Button>
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-white/10 pt-8">
              {STATS.map((s) => (
                <div key={s.label}>
                  <p className={`${HEADING} text-2xl font-extrabold text-white sm:text-3xl`}>{s.value}</p>
                  <p className="mt-1 text-xs text-slate-400 sm:text-sm">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative lg:col-span-5">
            <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-slate-900/60 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
              <img src={HERO_IMG} alt="Siswa sekolah musik belajar piano" className="max-h-[80vh] w-full object-cover" data-testid="hero-image" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-white/20 bg-[#071731]/90 p-4 shadow-2xl backdrop-blur-xl sm:block">
              <p className="text-xs font-medium text-slate-400">SPP Terkumpul Bulan Ini</p>
              <p className={`${HEADING} text-2xl font-extrabold text-white`}>Rp 24.750.000</p>
              <p className="text-xs font-semibold text-[#00D1A8]">Naik 12% dari bulan lalu</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Fitur ===== */}
      <section id="fitur" className="border-y border-white/5 bg-[#040D1D] py-24">
        <div className="mx-auto max-w-6xl px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00D1A8]">Fitur Unggulan</p>
          <h2 className={`${HEADING} mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl`}>
            Semua yang dibutuhkan sekolah musik Anda
          </h2>
          <p className="mt-3 max-w-2xl text-slate-400">
            Dari pendaftaran siswa baru sampai neraca keuangan — administrasi beres, Anda fokus mengajar musik.
          </p>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="group rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-md transition-all duration-300 hover:-translate-y-1.5 hover:border-[#00D1A8]/50 hover:bg-white/[0.06] hover:shadow-[0_12px_30px_rgba(0,209,168,0.12)]"
                data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#00D1A8]/30 bg-gradient-to-br from-[#2563EB]/20 to-[#00D1A8]/20 text-[#00D1A8] transition-transform duration-300 group-hover:scale-110">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className={`${HEADING} mt-5 text-lg font-semibold text-white`}>{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Harga ===== */}
      <section id="harga" className="bg-[#071731] py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00D1A8]">Harga</p>
            <h2 className={`${HEADING} mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl`}>
              Harga sederhana, tanpa biaya tersembunyi
            </h2>
            <p className="mt-3 text-slate-400">Pilih paket sesuai ukuran sekolah Anda. Bisa dibayar bulanan atau tahunan.</p>
            {cfg.promo_ends_at && <div><PromoCountdown endsAt={cfg.promo_ends_at} /></div>}
            <div className="mt-7 inline-flex rounded-full border border-white/15 bg-white/[0.06] p-1.5 backdrop-blur-md" data-testid="billing-toggle">
              <button
                onClick={() => setYearly(false)}
                data-testid="toggle-monthly"
                className={!yearly
                  ? "rounded-full bg-gradient-to-r from-[#2563EB] to-[#00D1A8] px-5 py-2 text-sm font-semibold text-white shadow-md"
                  : "rounded-full px-5 py-2 text-sm font-medium text-slate-400 transition-colors hover:text-white"}
              >
                Bulanan
              </button>
              <button
                onClick={() => setYearly(true)}
                data-testid="toggle-yearly"
                className={yearly
                  ? "rounded-full bg-gradient-to-r from-[#2563EB] to-[#00D1A8] px-5 py-2 text-sm font-semibold text-white shadow-md"
                  : "rounded-full px-5 py-2 text-sm font-medium text-slate-400 transition-colors hover:text-white"}
              >
                Tahunan
              </button>
            </div>
          </div>
          <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-3">
            {cfg.plans.map((p) => {
              const popular = p.id === "pro";
              return (
                <div
                  key={p.id}
                  data-testid={`plan-card-${p.id}`}
                  className={popular
                    ? "relative z-10 flex scale-[1.03] flex-col justify-between rounded-2xl border-2 border-[#00D1A8] bg-gradient-to-b from-[#0B2A5B]/80 to-[#071731]/90 p-8 shadow-[0_0_40px_rgba(0,209,168,0.2)]"
                    : "flex flex-col justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-md transition-colors hover:border-white/20"}
                >
                  <div>
                    {popular && (
                      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-[#2563EB] to-[#00D1A8] px-4 py-1 text-xs font-bold text-white shadow-lg">
                        Paling Populer
                      </span>
                    )}
                    <h3 className={`${HEADING} text-xl font-bold text-white`}>{p.name}</h3>
                    <div className="mt-4">
                      {(yearly ? p.original_yearly : p.original_monthly) && (
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-base text-slate-500 line-through" data-testid={`plan-original-price-${p.id}`}>
                            {rupiah(yearly ? p.original_yearly : p.original_monthly)}
                          </span>
                          <span className="rounded-full bg-[#00D1A8]/15 px-2 py-0.5 text-[11px] font-bold text-[#00D1A8]" data-testid={`plan-discount-${p.id}`}>
                            Hemat {rupiah((yearly ? p.original_yearly - p.yearly : p.original_monthly - p.monthly))}
                          </span>
                        </div>
                      )}
                      <span className={`${HEADING} text-4xl font-extrabold text-white`} data-testid={`plan-price-${p.id}`}>
                        {rupiah(yearly ? p.yearly : p.monthly)}
                      </span>
                      <span className="text-slate-400">/{yearly ? "tahun" : "bulan"}</span>
                    </div>
                    {yearly && (
                      <p className="mt-1 text-xs font-semibold text-[#00D1A8]">
                        Setara {rupiah(Math.round(p.yearly / 12))}/bulan
                      </p>
                    )}
                    <ul className="mt-7 space-y-3">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#00D1A8]" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link to="/daftar" className="mt-8 block">
                    <Button
                      className={popular
                        ? "w-full rounded-xl bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_25px_rgba(0,209,168,0.35)] hover:opacity-95"
                        : "w-full rounded-xl border border-white/20 bg-white/10 font-semibold text-white hover:bg-white/20"}
                      data-testid={`plan-cta-${p.id}`}
                    >
                      Pilih {p.name}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
          <p className="mt-9 text-center text-sm text-slate-500">
            Pembayaran via transfer — akun diaktifkan tim kami maksimal 1×24 jam setelah pembayaran terkonfirmasi.
          </p>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-[#040D1D] px-6 py-24">
        <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl border border-[#00D1A8]/30 bg-gradient-to-r from-[#0B2A5B] via-[#071731] to-[#0B2A5B] p-12 text-center shadow-2xl">
          <div className="pointer-events-none absolute -top-24 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-[#00D1A8]/20 blur-3xl" />
          <h2 className={`${HEADING} relative text-2xl font-bold text-white sm:text-4xl`}>
            Siap merapikan administrasi sekolah musik Anda?
          </h2>
          <p className="relative mt-3 text-slate-300">
            Daftar sekarang — tim Nexora mengaktifkan akun Anda setelah pembayaran terkonfirmasi.
          </p>
          <div className="relative mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/daftar">
              <Button size="lg" className="rounded-xl bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_25px_rgba(0,209,168,0.35)] hover:opacity-95" data-testid="cta-register-button">
                Daftar Sekarang
              </Button>
            </Link>
            {wa && (
              <a href={wa} target="_blank" rel="noreferrer">
                <Button size="lg" variant="outline" className="gap-2 rounded-xl border-white/20 bg-white/10 text-white hover:bg-white/20" data-testid="cta-wa-button">
                  <MessageCircle className="h-4 w-4" /> Tanya via WhatsApp
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ===== Hubungi Kami ===== */}
      <section id="kontak" className="bg-[#071731] py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00D1A8]">Hubungi Kami</p>
          <h2 className={`${HEADING} mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl`}>
            Ada pertanyaan? Tim kami siap membantu
          </h2>
          <p className="mt-3 text-slate-400">
            Konsultasi kebutuhan sekolah musik Anda atau konfirmasi pembayaran langsung via WhatsApp.
          </p>
          <div className="mx-auto mt-8 flex max-w-md flex-col items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-md" data-testid="contact-section">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[#00D1A8]/30 bg-[#00D1A8]/10">
              <MessageCircle className="h-7 w-7 text-[#00D1A8]" />
            </div>
            <p className={`${HEADING} text-2xl font-extrabold tracking-tight text-white`} data-testid="contact-wa-number">
              {formatPhone(cfg.owner_wa)}
            </p>
            {wa && (
              <a href={wa} target="_blank" rel="noreferrer" className="w-full">
                <Button className="w-full gap-2 rounded-xl bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_20px_rgba(0,209,168,0.3)] hover:opacity-95" data-testid="contact-wa-button">
                  <MessageCircle className="h-4 w-4" /> Chat via WhatsApp
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="border-t border-white/10 bg-[#040D1D] py-12">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-5 px-6 text-center">
          <BrandLockup compact />
          <p className="text-sm text-slate-400">
            © {new Date().getFullYear()} Aplikasi Sekolah Musik by Nexora — Digital • Technology • Innovation
          </p>
        </div>
      </footer>

      <ChatWidget />
    </div>
  );
}
